import { css } from "@linaria/core"
import { Paper, Stack, Typography } from "@mui/material"
import React, { useEffect } from "react"

import { integerEnforcer } from "../../../utils/format"
import TokenInput, { Input, SmallButton, TokenInputProps } from "../../TokenInput"

const INTERVALS = {
  "10_MIN": {
    runningTime: 10,
    minutes: "10",
    hours: "0",
  },
  "1_HOUR": {
    runningTime: 60,
    minutes: "0",
    hours: "1",
  },
  "4_HOURS": {
    runningTime: 4 * 60,
    minutes: "0",
    hours: "4",
  },
  "1_DAY": {
    runningTime: 24 * 60,
    minutes: "0",
    hours: "24",
  },
  "3_DAYS": {
    runningTime: 3 * 24 * 60,
    minutes: "0",
    hours: "72",
  },
  "7_DAYS": {
    runningTime: 7 * 24 * 60,
    minutes: "0",
    hours: "168",
  },
} as const

export type DcaOrderFormValues = {
  runningTime: number
  hours: string
  minutes: string
}

type DcaOrderFormProps = {
  onValuesChange?: (values: DcaOrderFormValues) => void
} & TokenInputProps

export function DcaOrderForm(props: DcaOrderFormProps) {
  const { onValuesChange, ...inputProps } = props

  const [runningTime, setRunningTime] = React.useState<number>(30)
  const [hours, setHours] = React.useState<string>("")
  const [minutes, setMinutes] = React.useState<string>("30")

  useEffect(() => {
    onValuesChange?.({ runningTime, hours, minutes })
  }, [])

  return (
    <Stack>
      <TokenInput {...inputProps} />
      <Paper
        sx={{
          padding: 1,
          marginBottom: 1,
          marginX: 1,
          marginTop: 0.25,
          border: "1px solid var(--mui-palette-borderSecondary)",
        }}
        variant="alt"
      >
        <Stack gap={1}>
          <Typography variant="body2" color="text.secondary">
            Running Time (5min-7days)
          </Typography>
          <Stack gap={1} direction="row">
            <Stack
              sx={{
                background: "var(--mui-palette-background-paper)",
                padding: 1,
                borderRadius: 1,
                flex: 1,
              }}
              gap={1}
              direction="row"
              alignItems="center"
            >
              <Input
                className={css`
                  font-size: 2rem;
                `}
                type="number"
                placeholder="0"
                value={hours}
                onChange={(event) => {
                  const nextInput = integerEnforcer(event.target.value.replace(/,/g, "."))
                  if (nextInput !== undefined) {
                    setHours(nextInput)
                    const newRunningTime = Number(minutes) + Number(nextInput) * 60
                    setRunningTime(newRunningTime)

                    onValuesChange?.({
                      runningTime: newRunningTime,
                      hours: nextInput,
                      minutes,
                    })
                  }
                }}
                pattern="^\d*$"
              />
              <span>Hour(s)</span>
            </Stack>
            <Stack
              sx={{
                background: "var(--mui-palette-background-paper)",
                padding: 1,
                borderRadius: 1,
                flex: 1,
              }}
              gap={1}
              direction="row"
              alignItems="center"
            >
              <Input
                className={css`
                  font-size: 2rem;
                `}
                type="number"
                placeholder="0"
                value={minutes}
                onChange={(event) => {
                  const nextInput = integerEnforcer(event.target.value.replace(/,/g, "."))
                  if (nextInput !== undefined) {
                    setMinutes(nextInput)
                    const newRunningTime = Number(nextInput) + Number(hours) * 60
                    setRunningTime(newRunningTime)

                    onValuesChange?.({
                      runningTime: newRunningTime,
                      hours,
                      minutes: nextInput,
                    })
                  }
                }}
                pattern="^\d*$"
              />
              <span>Minute(s)</span>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            sx={{
              alignSelf: "center",
              marginBottom: -0.25,
              "& button": {
                borderRadius: "3px !important",
              },
              "& button:not(.active)": {
                background: "none",
              },
            }}
          >
            <SmallButton
              className={runningTime === INTERVALS["10_MIN"].runningTime ? "active" : ""}
              onClick={() => {
                setRunningTime(INTERVALS["10_MIN"].runningTime)
                setHours(INTERVALS["10_MIN"].hours)
                setMinutes(INTERVALS["10_MIN"].minutes)
                onValuesChange?.(INTERVALS["10_MIN"])
              }}
            >
              10MIN
            </SmallButton>
            <SmallButton
              className={runningTime === INTERVALS["1_HOUR"].runningTime ? "active" : ""}
              onClick={() => {
                setRunningTime(INTERVALS["1_HOUR"].runningTime)
                setHours(INTERVALS["1_HOUR"].hours)
                setMinutes(INTERVALS["1_HOUR"].minutes)
                onValuesChange?.(INTERVALS["1_HOUR"])
              }}
            >
              1H
            </SmallButton>
            <SmallButton
              className={runningTime === INTERVALS["4_HOURS"].runningTime ? "active" : ""}
              onClick={() => {
                setRunningTime(INTERVALS["4_HOURS"].runningTime)
                setHours(INTERVALS["4_HOURS"].hours)
                setMinutes(INTERVALS["4_HOURS"].minutes)
                onValuesChange?.(INTERVALS["4_HOURS"])
              }}
            >
              4H
            </SmallButton>
            <SmallButton
              className={runningTime === INTERVALS["1_DAY"].runningTime ? "active" : ""}
              onClick={() => {
                setRunningTime(INTERVALS["1_DAY"].runningTime)
                setHours(INTERVALS["1_DAY"].hours)
                setMinutes(INTERVALS["1_DAY"].minutes)
                onValuesChange?.(INTERVALS["1_DAY"])
              }}
            >
              1D
            </SmallButton>
            <SmallButton
              className={runningTime === INTERVALS["3_DAYS"].runningTime ? "active" : ""}
              onClick={() => {
                setRunningTime(INTERVALS["3_DAYS"].runningTime)
                setHours(INTERVALS["3_DAYS"].hours)
                setMinutes(INTERVALS["3_DAYS"].minutes)
                onValuesChange?.(INTERVALS["3_DAYS"])
              }}
            >
              3D
            </SmallButton>
            <SmallButton
              className={runningTime === INTERVALS["7_DAYS"].runningTime ? "active" : ""}
              onClick={() => {
                setRunningTime(INTERVALS["7_DAYS"].runningTime)
                setHours(INTERVALS["7_DAYS"].hours)
                setMinutes(INTERVALS["7_DAYS"].minutes)
                onValuesChange?.(INTERVALS["7_DAYS"])
              }}
            >
              7D
            </SmallButton>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  )
}
